import React, { useEffect, useState } from 'react';
import { Box, Container, CircularProgress } from '@mui/material';
import { useLocation, Navigate } from 'react-router-dom';
import { legalPageService } from '../services/api';
import SimplePageBanner from '../components/SimplePageBanner';
import { BRAND } from '../theme/pageStyles';

const PATH_SLUG = {
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/refund': 'refund',
};

const FALLBACK = {
  terms: {
    title: 'Terms & Conditions',
    subtitle: 'Please read these terms before using our website or making a donation',
    content: '<p>Please check back shortly. Our terms are being published.</p>',
  },
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How Roots & Wings Foundation collects, uses, and protects your information',
    content: '<p>Please check back shortly. Our privacy policy is being published.</p>',
  },
  refund: {
    title: 'Refund and Cancellation Policy',
    subtitle: 'Donations, failed payments, recurring gifts, and how to reach us',
    content: '<p>Please check back shortly. Our refund policy is being published.</p>',
  },
};

const LegalPage = () => {
  const { pathname } = useLocation();
  const slug = PATH_SLUG[pathname];
  const [page, setPage] = useState(FALLBACK[slug] || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    legalPageService
      .getBySlug(slug)
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        if (!cancelled && data && data.title) {
          setPage({
            title: data.title,
            subtitle: data.subtitle || '',
            content: data.content || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!slug || !FALLBACK[slug]) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Our policies"
        title={page?.title || FALLBACK[slug].title}
        description={page?.subtitle || FALLBACK[slug].subtitle}
      />
      <Box sx={{ backgroundColor: '#FAF9F6', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: BRAND.green }} />
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: 2,
                border: '1px solid #EEECE1',
                p: { xs: 3, md: 5 },
                '& h2': {
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: 20, md: 22 },
                  color: '#2D2929',
                  mt: 3.5,
                  mb: 1.5,
                },
                '& p, & li': {
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: 15,
                  lineHeight: 1.8,
                  color: '#4B4646',
                },
                '& p': { mb: 1.5 },
                '& ul': { pl: 3, mb: 2 },
                '& a': { color: BRAND.green, fontWeight: 600 },
              }}
              dangerouslySetInnerHTML={{ __html: page?.content || FALLBACK[slug].content }}
            />
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default LegalPage;
